import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import salaryService from '../../services/SalaryService.js';
import { Wallet, Pencil, ArrowLeft, Eye } from 'lucide-react';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewSalary = () => {
  const { salaryId } = useParams();
  const [salary, setSalary] = useState(null);
  const [previousSalaries, setPreviousSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllPrevious, setShowAllPrevious] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSalaryDetails();
  }, [salaryId]);

  const fetchSalaryDetails = async () => {
    try {
      const [salaryRes, userSalariesRes] = await Promise.all([
        salaryService.getById(salaryId),
        getSalaryUserId()
      ]);
      
      setSalary(salaryRes.data);
      
      // Fetch all salaries for this user
      if (salaryRes.data.user_id) {
        const allSalariesRes = await salaryService.getByUserId(salaryRes.data.user_id);
        const otherSalaries = allSalariesRes.data.filter(s => s.salary_id !== salaryId);
        setPreviousSalaries(otherSalaries);
      }
    } catch (err) {
      Alert.error('Failed to load salary details');
      console.error(err);
      navigate('/salaries');
    } finally {
      setLoading(false);
    }
  };

  const getSalaryUserId = async () => {
    const response = await salaryService.getById(salaryId);
    return response.data.user_id;
  };

  const viewPaymentScreenshot = async () => {
    try {
      // Get the auth token from localStorage (same way as axios interceptor)
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = user.token;
      
      if (!token) {
        Alert.error('Authentication required. Please login again.');
        return;
      }
      
      // Construct screenshot URL
      const screenshotUrl = salaryService.getPaymentScreenshot(salaryId);
      
      // Fetch with authorization header
      const response = await fetch(screenshotUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load payment screenshot');
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create a temporary URL for the blob and open in new tab
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Optional: Clean up after a delay (browser will handle this anyway)
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      
    } catch (err) {
      Alert.error(err.message || 'Failed to view payment screenshot');
      console.error('Screenshot view error:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => <StatusBadge status={status} />;

  const calculateTotalEarnings = () => {
    if (!salary) return 0;
    return (salary.basic_salary || 0) + (salary.allowances || 0) + (salary.bonus || 0) + (salary.overtime_pay || 0);
  };

  const calculatePreviousTotal = () => {
    return previousSalaries.reduce((sum, s) => sum + (s.net_salary || 0), 0);
  };

  const displayedPreviousSalaries = showAllPrevious ? previousSalaries : previousSalaries.slice(0, 3);

  if (loading) {
    return (
      <PageLayout title="Salary Details" icon={Wallet}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading salary details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!salary) {
    return null;
  }

  return (
    <PageLayout
      title="Salary Details"
      icon={Wallet}
      actions={
        <>
          <Button
            variant="secondary"
            icon={Pencil}
            onClick={() => navigate(`/salaries/edit/${salaryId}`)}
          >
            Edit Salary
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/salaries')}>
            Back to Salaries
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="bg-primary rounded-card p-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-secondary">{salary.salary_id}</h2>
          <span className="text-sm text-gray-600">
            {salary.user_details?.name} - {salary.month_display} {salary.year}
          </span>
          <StatusBadge status={salary.status} />
        </div>

        <DetailCard title="Current Month Salary Breakdown">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="bg-green-50 p-3 rounded border border-green-200">
              <p className="text-xs text-gray-600 mb-1">Basic Salary</p>
              <p className="text-base font-bold text-green-700">{formatCurrency(salary.basic_salary)}</p>
            </div>
            <div className="bg-green-50 p-3 rounded border border-green-200">
              <p className="text-xs text-gray-600 mb-1">Allowances</p>
              <p className="text-base font-bold text-green-700">{formatCurrency(salary.allowances)}</p>
            </div>
            <div className="bg-green-50 p-3 rounded border border-green-200">
              <p className="text-xs text-gray-600 mb-1">Bonus</p>
              <p className="text-base font-bold text-green-700">{formatCurrency(salary.bonus)}</p>
            </div>
            <div className="bg-green-50 p-3 rounded border border-green-200">
              <p className="text-xs text-gray-600 mb-1">Overtime Pay</p>
              <p className="text-base font-bold text-green-700">{formatCurrency(salary.overtime_pay)}</p>
            </div>
          </div>
          <div className="bg-red-50 p-3 rounded border border-red-200 mb-4">
            <p className="text-xs text-gray-600 mb-1">Total Deductions</p>
            <p className="text-base font-bold text-red-700">{formatCurrency(salary.deductions)}</p>
          </div>
          <div className="bg-brand-light p-4 rounded border border-blue-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Total Earnings</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(calculateTotalEarnings())}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Total Deductions</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(salary.deductions)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Net Salary</p>
              <p className="text-xl font-bold text-brand">{formatCurrency(salary.net_salary)}</p>
            </div>
          </div>
        </DetailCard>

        <DetailCard title="Employee Information">
          <DetailRow label="Employee Name" value={salary.user_details?.name} />
          <DetailRow label="Email" value={salary.user_details?.email} />
          <DetailRow label="Role" value={salary.user_details?.role_name} />
          <DetailRow label="User ID" value={salary.user_id} />
        </DetailCard>

        <DetailCard title="Payment Information">
          <DetailRow
            label="Payment Mode"
            value={salary.payment_mode ? salary.payment_mode.replace(/_/g, ' ').toUpperCase() : 'N/A'}
          />
          <DetailRow label="Transaction ID" value={salary.transaction_id || 'N/A'} />
          <DetailRow label="Payment Date" value={formatDate(salary.payment_date)} />
          <DetailRow label="Credited By" value={salary.credited_by || 'N/A'} />
          {salary.has_screenshot && (
            <DetailRow
              label="Payment Screenshot"
              value={
                <Button variant="primary" size="sm" icon={Eye} onClick={viewPaymentScreenshot}>
                  View Payment Screenshot
                </Button>
              }
            />
          )}
        </DetailCard>

        {salary.remarks && (
          <DetailCard title="Remarks">
            <p className="text-sm text-gray-700">{salary.remarks}</p>
          </DetailCard>
        )}

        <DetailCard title="System Information">
          <DetailRow label="Created By" value={salary.created_by} />
          <DetailRow label="Created At" value={formatDate(salary.created_at)} />
          <DetailRow label="Last Updated" value={formatDate(salary.updated_at)} />
        </DetailCard>
      </div>

                {/* Previous Salary Records */}
                {previousSalaries.length > 0 && (
                  <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                        📋 Previous Salary Records ({previousSalaries.length})
                      </h3>
                      <p className="text-purple-100 text-sm mt-1">
                        Total Previous Payments: {formatCurrency(calculatePreviousTotal())}
                      </p>
                    </div>

                    <div className="p-4 sm:p-6">
                      {/* Desktop Table */}
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b-2 border-gray-200">
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Period</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Basic</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Allowances</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Deductions</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Net Salary</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {displayedPreviousSalaries.map((prevSalary) => (
                              <tr key={prevSalary.salary_id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">{prevSalary.month_year}</td>
                                <td className="px-4 py-3 text-sm font-medium">{formatCurrency(prevSalary.basic_salary)}</td>
                                <td className="px-4 py-3 text-sm font-medium text-green-600">{formatCurrency(prevSalary.allowances)}</td>
                                <td className="px-4 py-3 text-sm font-medium text-red-600">{formatCurrency(prevSalary.deductions)}</td>
                                <td className="px-4 py-3 text-sm font-bold text-brand">{formatCurrency(prevSalary.net_salary)}</td>
                                <td className="px-4 py-3">{getStatusBadge(prevSalary.status)}</td>
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => navigate(`/salaries/view/${prevSalary.salary_id}`)}
                                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="lg:hidden space-y-3">
                        {displayedPreviousSalaries.map((prevSalary) => (
                          <div key={prevSalary.salary_id} className="border border-gray-200 rounded-sm p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <p className="font-semibold text-gray-900">{prevSalary.month_year}</p>
                                <p className="text-sm text-gray-600">{prevSalary.salary_id}</p>
                              </div>
                              {getStatusBadge(prevSalary.status)}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <span className="text-gray-600">Basic:</span>
                                <p className="font-medium">{formatCurrency(prevSalary.basic_salary)}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Deductions:</span>
                                <p className="font-medium text-red-600">{formatCurrency(prevSalary.deductions)}</p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-600">Net Salary:</span>
                                <p className="font-bold text-brand text-base">{formatCurrency(prevSalary.net_salary)}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => navigate(`/salaries/view/${prevSalary.salary_id}`)}
                              className="w-full px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors text-sm font-medium"
                            >
                              View Details
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Show More/Less Button */}
                      {previousSalaries.length > 3 && (
                        <div className="mt-4 text-center">
                          <button
                            onClick={() => setShowAllPrevious(!showAllPrevious)}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300 transition-colors text-sm font-medium"
                          >
                            {showAllPrevious ? '↑ Show Less' : `↓ Show All (${previousSalaries.length - 3} more)`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
    </PageLayout>
  );
};

export default ViewSalary;